/**
 * Quiz Store
 *
 * Manages the current quiz state including questions, answers, and UI state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { IQuestion } from '@/types/question';
import { useSessionStore } from './sessionStore';

interface QuizState {
  // Question state
  currentQuestion: IQuestion | null;
  questionHistory: number[];

  // UI state
  isLoading: boolean;
  isVisible: boolean;
  showAnswer: boolean;
  error: string | null;

  // Timing
  questionStartTime: number | null;

  // Actions
  fetchQuestion: (topic: string, type: string, subtype?: string) => Promise<void>;
  setCurrentQuestion: (question: IQuestion) => void;
  toggleAnswer: () => void;
  hideAnswer: () => void;
  setVisibility: (visible: boolean) => void;
  submitAnswer: (answer: string, isCorrect: boolean) => Promise<void>;
  reset: () => void;

  // Sync from server (WebSocket)
  syncFromServer: (data: Partial<QuizState>) => void;
}

export const useQuizStore = create<QuizState>()(
  devtools(
    (set, get) => ({
      // Initial state
      currentQuestion: null,
      questionHistory: [],
      isLoading: false,
      isVisible: true,
      showAnswer: false,
      error: null,
      questionStartTime: null,

      // Fetch a new question from the API
      fetchQuestion: async (topic, type, subtype) => {
        const sessionId = useSessionStore.getState().sessionId;

        set({ isLoading: true, error: null, showAnswer: false });

        // Fade out animation
        set({ isVisible: false });
        await new Promise((resolve) => setTimeout(resolve, 100));

        try {
          const params = new URLSearchParams({ type });
          if (subtype) {
            params.append('subtype', subtype);
          }

          const headers: HeadersInit = {};
          if (sessionId) {
            headers['X-Session-ID'] = sessionId;
          }

          const response = await fetch(`/api/questions/${topic}?${params}`, {
            headers,
          });

          if (!response.ok) {
            throw new Error('Failed to fetch question');
          }

          const data = await response.json();
          const question = data.data as IQuestion;

          set({
            currentQuestion: question,
            questionHistory: [...get().questionHistory, question.id],
            isLoading: false,
            isVisible: true,
            questionStartTime: Date.now(),
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch question';
          set({ error: message, isLoading: false, isVisible: true });
        }
      },

      // Set current question directly (e.g., from WebSocket)
      setCurrentQuestion: (question) => {
        set({
          currentQuestion: question,
          questionHistory: [...get().questionHistory, question.id],
          questionStartTime: Date.now(),
          showAnswer: false,
        });
      },

      // Toggle answer visibility
      toggleAnswer: () => {
        set((state) => ({ showAnswer: !state.showAnswer }));
      },

      // Hide answer
      hideAnswer: () => {
        set({ showAnswer: false });
      },

      // Set visibility for fade animation
      setVisibility: (visible) => {
        set({ isVisible: visible });
      },

      // Submit an answer
      submitAnswer: async (answer, isCorrect) => {
        const { currentQuestion, questionStartTime } = get();
        const sessionId = useSessionStore.getState().sessionId;

        if (!currentQuestion || !sessionId) return;

        const timeSpent = questionStartTime ? Date.now() - questionStartTime : 0;

        try {
          const response = await fetch(`/api/sessions/${sessionId}/answers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Session-ID': sessionId,
            },
            body: JSON.stringify({
              questionId: currentQuestion.id,
              questionType: currentQuestion.type,
              answer,
              isCorrect,
              timeSpent,
            }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.data?.stats) {
              useSessionStore.getState().updateStats(data.data.stats);
            }
          }
        } catch (error) {
          console.error('Failed to submit answer:', error);
        }
      },

      // Reset quiz state
      reset: () => {
        set({
          currentQuestion: null,
          questionHistory: [],
          isLoading: false,
          isVisible: true,
          showAnswer: false,
          error: null,
          questionStartTime: null,
        });
      },

      // Sync state from server (WebSocket updates)
      syncFromServer: (data) => {
        set((state) => ({
          ...state,
          ...data,
        }));
      },
    }),
    { name: 'quiz-store' }
  )
);

// Selectors
export const selectCurrentQuestion = (state: QuizState) => state.currentQuestion;
export const selectIsLoading = (state: QuizState) => state.isLoading;
export const selectShowAnswer = (state: QuizState) => state.showAnswer;
export const selectIsVisible = (state: QuizState) => state.isVisible;
export const selectError = (state: QuizState) => state.error;
